import React, { useEffect, useMemo, useState } from 'react';
import { configFieldsResponse } from '../../../config/configSchema';
import { DeviceCreate } from '../../../api/models/DeviceCreate';
import { apiClient } from '../../../services/apiClient';
import { clientService } from '../../../services/clientService';
import { TemplateForDevice, deviceService } from '../../../services/deviceService';
import { typesService, HardwareType, ModelReference, HardwareFieldDefinition } from '../../../services/typesService';
import { Client } from '../../../types/client';
import { CustomField } from '../../../types/configTemplate';
import { useI18n } from '../../../contexts/I18nContext';
import { sortSchema } from '../../configTemplates/templateSchema';
import {
  CustomValues, DeviceFormData, FormErrors, buildCreatePayload, customFieldList, emptyDeviceForm, explainCreateError,
  formKeyOf, groupFields, hasDefaults, partitionValues, schemaDefaultValues, templateContribution, toDeviceCreate, validateDeviceForm,
} from './deviceCreateForm';

interface Options {
  isOpen: boolean;
  onClose: () => void;
  onDeviceCreated: (device: DeviceCreate) => void;
}

/**
 * The "new device" dialog: identity (client, type, model), the template
 * applied to it, and the fields of the type's schema. Reference lists load
 * when the dialog opens; the schema and the templates follow the type.
 */
export const useCreateDevice = ({ isOpen, onClose, onDeviceCreated }: Options) => {
  const { t } = useI18n();
  const [form, setForm] = useState<DeviceFormData>(emptyDeviceForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const [clients, setClients] = useState<Client[]>([]);
  const [hardwareTypes, setHardwareTypes] = useState<HardwareType[]>([]);
  const [modelRefs, setModelRefs] = useState<ModelReference[]>([]);

  const [templates, setTemplates] = useState<TemplateForDevice[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

  // null until a type is picked (or while its schema loads); [] when the type has no fields.
  const [schema, setSchema] = useState<HardwareFieldDefinition[] | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [customValues, setCustomValues] = useState<CustomValues>({});
  const [customFields, setCustomFields] = useState<CustomField[]>([]);

  const selectedType = useMemo(() => hardwareTypes.find((h) => h.code === form.deviceType), [hardwareTypes, form.deviceType]);

  // Reference lists, once the dialog opens. Only active types: a new device never goes on a retired type.
  useEffect(() => {
    if (!isOpen) return;
    clientService.fetchActiveClients().then(setClients).catch((err) => console.error('Failed to fetch clients:', err));
    typesService.getHardwareTypes().then(setHardwareTypes).catch((err) => console.error('Failed to fetch hardware types:', err));
  }, [isOpen]);

  // The models of the chosen type; the model resets when the type changes.
  useEffect(() => {
    if (selectedType) {
      typesService.getModelReferences(selectedType.id).then(setModelRefs).catch((err) => console.error('Failed to fetch model refs:', err));
    } else {
      setModelRefs([]);
    }
    setForm((prev) => ({ ...prev, model: '' }));
  }, [selectedType]);

  // The templates of the client for that type.
  useEffect(() => {
    if (!form.idClient || !form.deviceType) {
      setTemplates([]);
      setSelectedTemplateId(null);
      return;
    }
    setTemplatesLoading(true);
    apiClient.get(`/v2/config-templates?device_type=${form.deviceType}&client_id=${form.idClient}`)
      .then((data: any) => setTemplates(Array.isArray(data) ? data : (data.items || [])))
      .catch((err) => {
        console.error('Failed to fetch templates:', err);
        setTemplates([]);
      })
      .finally(() => setTemplatesLoading(false));
  }, [form.idClient, form.deviceType]);

  // The schema of the chosen type drives which fields the form shows. Fields start blank; defaults are placeholders.
  useEffect(() => {
    if (!form.deviceType) {
      setSchema(null);
      setCustomValues({});
      return;
    }
    if (!selectedType) {
      setSchema(null);
      return;
    }
    setSchemaLoading(true);
    Promise.resolve(configFieldsResponse(selectedType.id, selectedType.code))
      .then((response) => setSchema(sortSchema(response.items)))
      .catch((err) => {
        console.error('Failed to fetch hardware type schema:', err);
        setSchema([]);
      })
      .finally(() => setSchemaLoading(false));
  }, [form.deviceType, selectedType]);

  const groupedSchema = useMemo(() => groupFields(schema), [schema]);
  const canFillDefaults = useMemo(() => hasDefaults(schema), [schema]);

  const clearError = (name: string) => {
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const setField = (field: keyof DeviceFormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    clearError(field);
  };

  /** A schema field's value, wherever it lives. */
  const fieldValue = (def: HardwareFieldDefinition): string => {
    const key = formKeyOf(def.field_name);
    return String((key ? form[key] : customValues[def.field_name]) ?? '');
  };

  const setFieldValue = (def: HardwareFieldDefinition, value: string) => {
    const key = formKeyOf(def.field_name);
    if (key) setForm((prev) => ({ ...prev, [key]: value }));
    else setCustomValues((prev) => ({ ...prev, [def.field_name]: value }));
    clearError(def.field_name);
  };

  const applyValues = (values: Record<string, string>) => {
    const { formPatch, customPatch } = partitionValues(values);
    setForm((prev) => ({ ...prev, ...formPatch }));
    setCustomValues((prev) => ({ ...prev, ...customPatch }));
  };

  const fillDefaults = () => {
    if (schema) applyValues(schemaDefaultValues(schema));
  };

  /** Pours the template's configuration into the form. */
  const applyTemplate = async (templateId: number | null) => {
    setSelectedTemplateId(templateId);
    if (!templateId || !templates.some((tpl) => tpl.id === templateId)) return;
    try {
      const full = await apiClient.get(`/v2/config-templates/${templateId}`);
      const { formPatch, customPatch, password, customFields: fields } = templateContribution(full.json_config || {});
      setForm((prev) => ({ ...prev, ...formPatch, ...(password !== undefined ? { password } : {}) }));
      setCustomValues((prev) => ({ ...prev, ...customPatch }));
      if (fields.length > 0) setCustomFields(fields);
    } catch (err) {
      console.error('Failed to fetch template details:', err);
    }
  };

  const reset = () => {
    setForm(emptyDeviceForm());
    setSelectedTemplateId(null);
    setErrors({});
    setSchema(null);
    setCustomValues({});
    setCustomFields([]);
  };

  /**
   * Creates the device, then saves the custom values with a second call:
   * the create body cannot carry name/value pairs.
   */
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const found = validateDeviceForm(form, schema, customValues, t);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSubmitting(true);
    const device = toDeviceCreate(form);
    try {
      const response = await apiClient.getAxiosInstance().post('/v2/conf', buildCreatePayload(form, selectedTemplateId));
      if (response.status !== 200 && response.status !== 201) return;

      const extras = customFieldList(customValues);
      if (extras.length > 0) {
        try {
          await deviceService.overrideConfig(device.idDevice, { custom_fields: extras });
        } catch (overrideErr: any) {
          console.error('[CREATE DEVICE] Custom fields override failed:', overrideErr);
          const error = overrideErr?.response?.data?.detail || overrideErr?.message || t('devices.create.unknownError');
          setErrors({ submit: t('devices.create.customFieldsFailed', { error }) });
          return;
        }
      }
      onDeviceCreated(device);
      onClose();
      reset();
    } catch (error: any) {
      console.error('[CREATE DEVICE] Error:', error);
      setErrors({ submit: explainCreateError(error, form, t) });
    } finally {
      setSubmitting(false);
    }
  };

  return {
    form, errors, submitting, setField, dismissSubmitError: () => setErrors((prev) => ({ ...prev, submit: '' })),
    clients, hardwareTypes, modelRefs,
    templates, templatesLoading, selectedTemplateId, applyTemplate,
    schema, schemaLoading, groupedSchema, canFillDefaults, fieldValue, setFieldValue, fillDefaults,
    customFields, setCustomFields,
    submit,
  };
};

export type CreateDeviceState = ReturnType<typeof useCreateDevice>;
